import React, { useEffect, useRef } from "react";
import { pickColor, rgba, smoothNoise } from "./internal/colorUtils";

export const key = "flow-field";

const DEFAULT_BG = "#0a0d18";
const DEFAULT_PARTICLE_A = "#22d3ee";
const DEFAULT_PARTICLE_B = "#f472b6";

const PARTICLE_DENSITY = 0.00018;
const MIN_PARTICLES = 80;
const MAX_PARTICLES = 360;
const SPEED = 1.1;
const NOISE_SCALE = 0.0028;     // smaller = larger smoother swirls
const TIME_SCALE = 0.0009;      // how fast the field itself evolves
const FADE_ALPHA = 0.06;        // motion-blur trail length (lower = longer)
const PARTICLE_LIFE = 220;      // frames before respawn (keeps trails fresh)

export default function FlowField({ colors = [], style }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const particlesRef = useRef([]);
  const colorsRef = useRef([DEFAULT_BG, DEFAULT_PARTICLE_A, DEFAULT_PARTICLE_B]);

  useEffect(() => {
    colorsRef.current = [
      pickColor(colors, 0, DEFAULT_BG),
      pickColor(colors, 1, DEFAULT_PARTICLE_A),
      pickColor(colors, 2, DEFAULT_PARTICLE_B),
    ];
  }, [colors]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let width = 0;
    let height = 0;
    const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;
    let t = 0;
    let primed = false; // first frame: paint the bg solid (no fade) to prevent flash

    const seedParticles = () => {
      const target = Math.round(width * height * PARTICLE_DENSITY);
      const count = Math.max(MIN_PARTICLES, Math.min(MAX_PARTICLES, target));
      const arr = new Array(count);
      for (let i = 0; i < count; i += 1) {
        arr[i] = newParticle(true);
      }
      particlesRef.current = arr;
    };

    const newParticle = (initial = false) => ({
      x: Math.random() * width,
      y: Math.random() * height,
      // Each particle picks one of the two accent colors so the canvas reads
      // as two intermixed currents rather than a single hue.
      slot: Math.random() < 0.5 ? 1 : 2,
      life: initial ? Math.random() * PARTICLE_LIFE : 0,
    });

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      // Use clientWidth/Height (layout dims) so an ancestor `transform: scale(...)`
      // (e.g. PreviewDeviceStage) doesn't shrink the canvas and pin it to top-left.
      width = Math.max(1, parent.clientWidth || parent.offsetWidth || 0);
      height = Math.max(1, parent.clientHeight || parent.offsetHeight || 0);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seedParticles();
      primed = false;
    };

    const tick = () => {
      const [bg, cA, cB] = colorsRef.current;

      // Fade previous frame to background — produces the trailing comet look.
      if (!primed) {
        ctx.fillStyle = bg;
        ctx.fillRect(0, 0, width, height);
        primed = true;
      } else {
        ctx.fillStyle = rgba(bg, FADE_ALPHA);
        ctx.fillRect(0, 0, width, height);
      }

      const particles = particlesRef.current;
      ctx.lineWidth = 1;
      ctx.lineCap = "round";

      for (let i = 0; i < particles.length; i += 1) {
        const p = particles[i];
        const angle = smoothNoise(p.x * NOISE_SCALE, p.y * NOISE_SCALE, t * TIME_SCALE) * Math.PI * 2;
        const nx = p.x + Math.cos(angle) * SPEED;
        const ny = p.y + Math.sin(angle) * SPEED;

        ctx.strokeStyle = rgba(p.slot === 1 ? cA : cB, 0.55);
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
        ctx.lineTo(nx, ny);
        ctx.stroke();

        p.x = nx;
        p.y = ny;
        p.life += 1;

        // Respawn if lifespan exceeded or out of bounds — keeps coverage
        // even after the field's vector pattern shifts.
        if (
          p.life > PARTICLE_LIFE ||
          p.x < -10 || p.x > width + 10 ||
          p.y < -10 || p.y > height + 10
        ) {
          particles[i] = newParticle();
        }
      }

      t += 1;
      rafRef.current = requestAnimationFrame(tick);
    };

    resize();
    rafRef.current = requestAnimationFrame(tick);

    let ro = null;
    if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
      ro = new ResizeObserver(resize);
      ro.observe(canvas.parentElement);
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", resize);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ro) {
        try { ro.disconnect(); } catch { /* noop */ }
      } else if (typeof window !== "undefined") {
        window.removeEventListener("resize", resize);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        pointerEvents: "none",
        ...style,
      }}
    />
  );
}
